import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { 
  Plus, 
  Edit, 
  Trash2, 
  GripVertical, 
  Eye, 
  Users, 
  Briefcase, 
  CheckCircle2,
  Settings,
  PlayCircle,
  FileText,
  Video,
  Link as LinkIcon,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Onboarding = () => {
  const [loading, setLoading] = useState(true);
  const [platformOnboarding, setPlatformOnboarding] = useState(null);
  const [newHireWizards, setNewHireWizards] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [wizardDialogOpen, setWizardDialogOpen] = useState(false);
  const [stepDialogOpen, setStepDialogOpen] = useState(false);
  const [selectedWizard, setSelectedWizard] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);

  const [wizardForm, setWizardForm] = useState({
    name: '',
    description: '',
    target_role: '',
    is_active: true,
    steps: []
  });

  const [stepForm, setStepForm] = useState({
    title: '',
    description: '',
    type: 'document',
    content: '',
    video_url: '',
    document_url: '',
    required: true,
    order: 0
  });

  useEffect(() => {
    fetchOnboardingData();
  }, []);

  const fetchOnboardingData = async () => {
    try {
      const [platformRes, wizardsRes] = await Promise.all([
        axios.get(`${API}/onboarding/platform`),
        axios.get(`${API}/onboarding/wizards`)
      ]);
      
      setPlatformOnboarding(platformRes.data);
      setNewHireWizards(wizardsRes.data || []);
    } catch (error) {
      console.error('Error fetching onboarding data:', error);
      toast.error('Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlatformOnboarding = async () => {
    try {
      await axios.put(`${API}/onboarding/platform`, platformOnboarding);
      toast.success('Platform onboarding updated');
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating platform onboarding:', error);
      toast.error('Failed to update platform onboarding');
    }
  };

  const handleSaveWizard = async () => {
    try {
      if (selectedWizard) {
        // Update existing
        await axios.put(`${API}/onboarding/wizards/${selectedWizard.id}`, wizardForm);
        toast.success('Wizard updated successfully');
      } else {
        // Create new
        await axios.post(`${API}/onboarding/wizards`, wizardForm);
        toast.success('Wizard created successfully');
      }
      
      setWizardDialogOpen(false);
      setWizardForm({
        name: '',
        description: '',
        target_role: '',
        is_active: true,
        steps: []
      });
      fetchOnboardingData();
    } catch (error) {
      console.error('Error saving wizard:', error);
      toast.error('Failed to save wizard');
    }
  };

  const handleDeleteWizard = async (wizardId) => {
    if (!confirm('Are you sure you want to delete this wizard?')) return;
    
    try {
      await axios.delete(`${API}/onboarding/wizards/${wizardId}`);
      toast.success('Wizard deleted');
      fetchOnboardingData();
    } catch (error) {
      console.error('Error deleting wizard:', error);
      toast.error('Failed to delete wizard');
    }
  };

  const handleAddStep = () => {
    const newStep = {
      ...stepForm,
      id: Date.now().toString(),
      order: wizardForm.steps.length
    };
    
    setWizardForm({
      ...wizardForm,
      steps: [...wizardForm.steps, newStep]
    });
    
    setStepDialogOpen(false);
    setStepForm({
      title: '',
      description: '',
      type: 'document',
      content: '',
      video_url: '',
      document_url: '',
      required: true,
      order: 0
    });
  };

  const handleDeleteStep = (stepId) => {
    setWizardForm({
      ...wizardForm,
      steps: wizardForm.steps.filter(s => s.id !== stepId)
    });
  };

  const openEditWizard = (wizard) => {
    setSelectedWizard(wizard);
    setWizardForm(wizard);
    setWizardDialogOpen(true);
  };

  const openNewWizard = () => {
    setSelectedWizard(null);
    setWizardForm({
      name: '',
      description: '',
      target_role: '',
      is_active: true,
      steps: []
    });
    setWizardDialogOpen(true);
  };

  const getStepIcon = (type) => {
    switch(type) {
      case 'video': return <Video className="h-4 w-4" />;
      case 'document': return <FileText className="h-4 w-4" />;
      case 'link': return <LinkIcon className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading onboarding configuration...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Onboarding Management</h1>
          <p className="text-muted-foreground">Configure user onboarding and new hire wizard workflows</p>
        </div>

        <Tabs defaultValue="platform" className="space-y-6">
          <TabsList>
            <TabsTrigger value="platform" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Platform Onboarding
            </TabsTrigger>
            <TabsTrigger value="newhire" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              New Hire Wizards
            </TabsTrigger>
          </TabsList>

          {/* Platform Onboarding Tab */}
          <TabsContent value="platform" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Platform User Onboarding</CardTitle>
                    <CardDescription>
                      Configure the onboarding experience for new users accessing the platform
                    </CardDescription>
                  </div>
                  <Button onClick={() => setEditDialogOpen(true)}>
                    <Settings className="h-4 w-4 mr-2" />
                    Configure
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {platformOnboarding ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-muted-foreground">Welcome Message</Label>
                        <p className="font-medium">{platformOnboarding.welcome_message || 'Not configured'}</p>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Status</Label>
                        <div>
                          <Badge variant={platformOnboarding.is_enabled ? 'default' : 'secondary'}>
                            {platformOnboarding.is_enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm text-muted-foreground mb-2">Onboarding Steps</Label>
                      <div className="space-y-2">
                        {platformOnboarding.steps?.map((step, index) => (
                          <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                            <div className="bg-primary text-primary-foreground rounded-full h-6 w-6 flex items-center justify-center text-xs font-semibold">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{step.title}</p>
                              <p className="text-sm text-muted-foreground">{step.description}</p>
                            </div>
                            {step.required && (
                              <Badge variant="outline" className="text-xs">Required</Badge>
                            )}
                          </div>
                        )) || <p className="text-sm text-muted-foreground">No steps configured</p>}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Platform onboarding not configured</p>
                    <Button className="mt-4" onClick={() => setEditDialogOpen(true)}>
                      Configure Now
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* New Hire Wizards Tab */}
          <TabsContent value="newhire" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">New Hire Onboarding Wizards</h2>
                <p className="text-sm text-muted-foreground">Create role-specific onboarding workflows</p>
              </div>
              <Button onClick={openNewWizard}>
                <Plus className="h-4 w-4 mr-2" />
                Create Wizard
              </Button>
            </div>

            {newHireWizards.length === 0 ? (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Onboarding Wizards</h3>
                    <p className="text-muted-foreground mb-4">
                      Create your first new hire onboarding wizard to streamline the onboarding process
                    </p>
                    <Button onClick={openNewWizard}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create First Wizard
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {newHireWizards.map((wizard) => (
                  <Card key={wizard.id} className={!wizard.is_active ? 'opacity-60' : ''}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="flex items-center gap-2">
                            {wizard.name}
                            {wizard.is_active ? (
                              <Badge variant="default" className="text-xs">Active</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">Inactive</Badge>
                            )}
                          </CardTitle>
                          <CardDescription>{wizard.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Target Role:</span>
                        <Badge variant="outline">{wizard.target_role || 'All Roles'}</Badge>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Steps</span>
                          <span className="text-xs text-muted-foreground">
                            {wizard.steps?.length || 0} step{wizard.steps?.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="space-y-1">
                          {wizard.steps?.slice(0, 3).map((step, index) => (
                            <div key={index} className="flex items-center gap-2 text-sm p-2 bg-muted rounded">
                              {getStepIcon(step.type)}
                              <span className="truncate">{step.title}</span>
                            </div>
                          ))}
                          {wizard.steps?.length > 3 && (
                            <p className="text-xs text-muted-foreground text-center py-1">
                              +{wizard.steps.length - 3} more steps
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex gap-2 pt-2 border-t">
                        <Button variant="outline" size="sm" className="flex-1" onClick={() => openEditWizard(wizard)}>
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDeleteWizard(wizard.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Platform Onboarding Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Configure Platform Onboarding</DialogTitle>
              <DialogDescription>
                Set up the onboarding experience for new users
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable Onboarding</Label>
                  <p className="text-xs text-muted-foreground">Show onboarding to new users</p>
                </div>
                <Switch
                  checked={platformOnboarding?.is_enabled || false}
                  onCheckedChange={(checked) => 
                    setPlatformOnboarding({...platformOnboarding, is_enabled: checked})
                  }
                />
              </div>

              <div>
                <Label>Welcome Message</Label>
                <Input
                  value={platformOnboarding?.welcome_message || ''}
                  onChange={(e) => 
                    setPlatformOnboarding({...platformOnboarding, welcome_message: e.target.value})
                  }
                  placeholder="Welcome to the platform!"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={platformOnboarding?.description || ''}
                  onChange={(e) => 
                    setPlatformOnboarding({...platformOnboarding, description: e.target.value})
                  }
                  placeholder="Brief description of what users will learn..."
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSavePlatformOnboarding}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Wizard Create/Edit Dialog */}
        <Dialog open={wizardDialogOpen} onOpenChange={setWizardDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedWizard ? 'Edit' : 'Create'} Onboarding Wizard</DialogTitle>
              <DialogDescription>
                {selectedWizard ? 'Update the wizard configuration' : 'Create a new role-specific onboarding wizard'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <Label>Wizard Name *</Label>
                  <Input
                    value={wizardForm.name}
                    onChange={(e) => setWizardForm({...wizardForm, name: e.target.value})}
                    placeholder="e.g., Sales Representative Onboarding"
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={wizardForm.description}
                    onChange={(e) => setWizardForm({...wizardForm, description: e.target.value})}
                    placeholder="Brief description of this onboarding wizard..."
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Target Role</Label>
                    <Select
                      value={wizardForm.target_role}
                      onValueChange={(value) => setWizardForm({...wizardForm, target_role: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select role..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sales">Sales</SelectItem>
                        <SelectItem value="operations">Operations</SelectItem>
                        <SelectItem value="marketing">Marketing</SelectItem>
                        <SelectItem value="management">Management</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between pt-6">
                    <Label>Active</Label>
                    <Switch
                      checked={wizardForm.is_active}
                      onCheckedChange={(checked) => setWizardForm({...wizardForm, is_active: checked})}
                    />
                  </div>
                </div>
              </div>

              {/* Steps */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label>Onboarding Steps</Label>
                  <Button size="sm" onClick={() => setStepDialogOpen(true)}>
                    <Plus className="h-3 w-3 mr-1" />
                    Add Step
                  </Button>
                </div>

                {wizardForm.steps.length === 0 ? (
                  <div className="border-2 border-dashed rounded-lg p-8 text-center">
                    <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No steps added yet</p>
                    <Button size="sm" variant="outline" className="mt-2" onClick={() => setStepDialogOpen(true)}>
                      Add First Step
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {wizardForm.steps.map((step, index) => (
                      <div key={step.id} className="flex items-center gap-3 p-3 border rounded-lg bg-card">
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                        <div className="bg-primary text-primary-foreground rounded-full h-6 w-6 flex items-center justify-center text-xs font-semibold flex-shrink-0">
                          {index + 1}
                        </div>
                        {getStepIcon(step.type)}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{step.title}</p>
                          <p className="text-xs text-muted-foreground truncate">{step.description}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{step.type}</Badge>
                          {step.required && (
                            <Badge variant="outline" className="text-xs">Required</Badge>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleDeleteStep(step.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setWizardDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveWizard}>
                {selectedWizard ? 'Update' : 'Create'} Wizard
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Step Dialog */}
        <Dialog open={stepDialogOpen} onOpenChange={setStepDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Onboarding Step</DialogTitle>
              <DialogDescription>Create a new step in the onboarding wizard</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Step Title *</Label>
                <Input
                  value={stepForm.title}
                  onChange={(e) => setStepForm({...stepForm, title: e.target.value})}
                  placeholder="e.g., Watch Welcome Video"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={stepForm.description}
                  onChange={(e) => setStepForm({...stepForm, description: e.target.value})}
                  placeholder="Brief description of this step..."
                  rows={2}
                />
              </div>

              <div>
                <Label>Step Type *</Label>
                <Select
                  value={stepForm.type}
                  onValueChange={(value) => setStepForm({...stepForm, type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="document">Document/Text</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="link">External Link</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {stepForm.type === 'video' && (
                <div>
                  <Label>Video URL</Label>
                  <Input
                    value={stepForm.video_url}
                    onChange={(e) => setStepForm({...stepForm, video_url: e.target.value})}
                    placeholder="https://youtube.com/..."
                  />
                </div>
              )}

              {stepForm.type === 'document' && (
                <div>
                  <Label>Content</Label>
                  <Textarea
                    value={stepForm.content}
                    onChange={(e) => setStepForm({...stepForm, content: e.target.value})}
                    placeholder="Enter the content or instructions..."
                    rows={4}
                  />
                </div>
              )}

              {stepForm.type === 'link' && (
                <div>
                  <Label>Link URL</Label>
                  <Input
                    value={stepForm.document_url}
                    onChange={(e) => setStepForm({...stepForm, document_url: e.target.value})}
                    placeholder="https://..."
                  />
                </div>
              )}

              <div className="flex items-center justify-between">
                <div>
                  <Label>Required Step</Label>
                  <p className="text-xs text-muted-foreground">User must complete this step</p>
                </div>
                <Switch
                  checked={stepForm.required}
                  onCheckedChange={(checked) => setStepForm({...stepForm, required: checked})}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStepDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddStep}>Add Step</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
